import projectKeys from '@/api/queries/project/keyFactory';
import { Button, Modal } from '@payaca/ambrosia';
import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import * as materialsListActions from '@payaca/store/materialsList/materialsListActions';
import { useQueryClient } from '@tanstack/react-query';
import { FunctionComponent, useCallback, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

type Props = {
  materialsListId: number;
  projectPublicId?: string;
};

const ClearMaterialsListControl: FunctionComponent<Props> = ({
  materialsListId,
  projectPublicId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();

  const [showConfirmClearPrompt, setShowConfirmClearPrompt] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const queryClient = useQueryClient();

  const handleClearMaterialsList = useCallback(() => {
    setIsClearing(true);

    dispatch(
      materialsListActions.clearMaterialsList.request({
        materialsListId,
        callback: (response) => {
          dispatch(
            materialsListActions.requestGetMaterialsListWithRelatedEntities(
              materialsListId,
              () => {
                setIsClearing(false);
                setShowConfirmClearPrompt(false);
              }
            )
          );
          if (projectPublicId) {
            void queryClient.invalidateQueries({
              queryKey: projectKeys.profitBreakdown(projectPublicId),
            });
          }
        },
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dispatch', and 'queryClient'
  }, [materialsListId, projectPublicId]);

  return (
    <PermissionGuard
      renderIfHasPermissions={[MaterialsListPermissions.CLEAR_MATERIALS_LIST]}
    >
      <div className="clear-materials-list-control">
        <Button
          variant="secondary"
          colour="negative"
          size="sm"
          onClick={() => setShowConfirmClearPrompt(true)}
        >
          Clear the materials required
        </Button>

        <Modal
          title={'Are you sure?'}
          isOpen={showConfirmClearPrompt}
          onClose={() => setShowConfirmClearPrompt(false)}
        >
          <Modal.Body>
            <p>
              Materials which have already been purchased or included in a
              purchase order will not be cleared.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button
                colour="negative"
                onClick={handleClearMaterialsList}
                isProcessing={isClearing}
                replaceContentWhenProcessing
              >
                Clear materials required
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </Modal>
      </div>
    </PermissionGuard>
  );
};

export default ClearMaterialsListControl;
